import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobAccountPageShell } from './MobAccountPageShell';
import { fetchCreditsProfile, formatCreditDate } from '../../utils/creditsApi';
import { CreditTransactionDetails } from '../CreditTransactionDetails';

const EARN_OPPORTUNITIES = [
    {
        id: 'welcome',
        icon: 'bi-gift-fill',
        title: 'Welcome bonus',
        description: 'Get ₹200 store credit when you register and verify your account.',
        badge: '₹200',
        path: '/auth/register',
        cta: 'Register',
    },
    {
        id: 'cashback',
        icon: 'bi-bag-check-fill',
        title: 'Order cashback',
        description: 'Earn 5% store credit on delivered orders — credited 7 days after delivery (higher rates for VIP).',
        badge: '5% back',
        path: '/',
        cta: 'Shop now',
    },
    {
        id: 'vip',
        icon: 'bi-award-fill',
        title: 'VIP membership',
        description: 'Gold & Platinum members earn up to 1.5× cashback on purchases.',
        badge: 'Up to 1.5×',
        path: '/vip',
        cta: 'View VIP',
    },
    {
        id: 'refer',
        icon: 'bi-people-fill',
        title: 'Refer a friend',
        description: 'You both get credit when they sign up and place their first order.',
        badge: '₹50 each',
        path: null,
        cta: null,
    },
];

const CHECKOUT_STEPS = [
    { step: 1, text: 'Add items to cart and go to order summary.' },
    { step: 2, text: 'Toggle “Use store credit” before payment.' },
    { step: 3, text: 'Credits apply to your order total (up to your balance).' },
];

const GOOD_TO_KNOW = [
    {
        icon: 'bi-wallet2',
        title: 'Where to use',
        text: 'Credits apply at checkout on order summary, not on cart alone.',
    },
    {
        icon: 'bi-hourglass-split',
        title: 'When you earn',
        text: 'Cashback is credited 7 days after delivery, not when you place the order.',
    },
    {
        icon: 'bi-shield-check',
        title: 'No expiry',
        text: 'Your balance stays in your account until you redeem it on an order.',
    },
    {
        icon: 'bi-slash-circle',
        title: 'Limits',
        text: 'Credits cannot be withdrawn as cash or transferred to another account.',
    },
];

export function MobMyCredits() {
    const navigate = useNavigate();
    const [activityTab, setActivityTab] = useState('all');
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isGuest =
        localStorage.getItem('guestUser') === 'true' || !localStorage.getItem('token');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (isGuest) {
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const type =
                    activityTab === 'earned' ? 'earned' : activityTab === 'used' ? 'used' : undefined;
                const data = await fetchCreditsProfile({ type });
                if (!cancelled) setProfile(data);
            } catch (_e) {
                if (!cancelled) {
                    setError('Could not load store credit.');
                    setProfile(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isGuest, activityTab]);

    const balance = profile?.balance ?? 0;
    const filteredTransactions = useMemo(() => {
        return (profile?.transactions ?? []).map((txn) => ({
            ...txn,
            date: formatCreditDate(txn.date),
        }));
    }, [profile]);

    if (isGuest) {
        return (
            <MobAccountPageShell title="My Credit">
                <div className="mob-account-card mob-credits-guest-banner">
                    <p className="mob-credits-guest-banner__text">Sign in to view and use store credit.</p>
                    <button
                        type="button"
                        className="mob-account-cta mob-account-cta--inline"
                        onClick={() => navigate('/auth/login')}
                    >
                        Sign in
                    </button>
                </div>
            </MobAccountPageShell>
        );
    }

    return (
        <MobAccountPageShell title="My Credit">
            <div className="mob-credits-page">
                {error && <p className="mob-vip-error" role="status">{error}</p>}
                <header className="mob-credits-hero" aria-label="Credit balance">
                    <div className="mob-credits-hero__icon" aria-hidden>
                        <i className="bi bi-wallet2" />
                    </div>
                    <div className="mob-credits-hero__main">
                        <p className="mob-credits-hero__label">Available balance</p>
                        <p className="mob-credits-hero__value">
                            {loading ? '…' : `₹${balance.toLocaleString('en-IN')}`}
                        </p>
                        <p className="mob-credits-hero__sub">Redeem at checkout on your next order</p>
                    </div>
                </header>

                <div className="mob-credits-stats" aria-label="Credit summary">
                    <div className="mob-credits-stats__item">
                        <span className="mob-credits-stats__label">Earned (lifetime)</span>
                        <span className="mob-credits-stats__value mob-credits-stats__value--credit">
                            +₹{(profile?.lifetimeEarned ?? 0).toLocaleString('en-IN')}
                        </span>
                    </div>
                    <div className="mob-credits-stats__item">
                        <span className="mob-credits-stats__label">Used (lifetime)</span>
                        <span className="mob-credits-stats__value mob-credits-stats__value--debit">
                            −₹{(profile?.lifetimeUsed ?? 0).toLocaleString('en-IN')}
                        </span>
                    </div>
                    <div className="mob-credits-stats__item mob-credits-stats__item--highlight">
                        <span className="mob-credits-stats__label">Available now</span>
                        <span className="mob-credits-stats__value">
                            {loading ? '…' : `₹${balance.toLocaleString('en-IN')}`}
                        </span>
                    </div>
                </div>

                <nav className="mob-credits-quick" aria-label="Quick links">
                    <button type="button" className="mob-credits-quick__btn" onClick={() => navigate('/coupons')}>
                        <i className="bi bi-tag-fill" aria-hidden />
                        <span>Coupons</span>
                    </button>
                    <button type="button" className="mob-credits-quick__btn" onClick={() => navigate('/vip')}>
                        <i className="bi bi-award-fill" aria-hidden />
                        <span>VIP perks</span>
                    </button>
                    <button type="button" className="mob-credits-quick__btn" onClick={() => navigate('/cart')}>
                        <i className="bi bi-cart-fill" aria-hidden />
                        <span>Cart</span>
                    </button>
                </nav>

                <section className="mob-account-card mob-credits-card mob-credits-activity-section">
                    <h2 className="mob-account-card__title">
                        <i className="bi bi-clock-history" aria-hidden />
                        Recent activity
                    </h2>

                    <div className="mob-credits-activity-tabs" role="tablist" aria-label="Activity filter">
                        {[
                            { id: 'all', label: 'All' },
                            { id: 'earned', label: 'Earned' },
                            { id: 'used', label: 'Used' },
                        ].map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={activityTab === t.id}
                                className={`mob-credits-activity-tabs__btn${
                                    activityTab === t.id ? ' mob-credits-activity-tabs__btn--active' : ''
                                }`}
                                onClick={() => setActivityTab(t.id)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <p className="mob-credits-empty-activity">Loading activity…</p>
                    ) : filteredTransactions.length === 0 ? (
                        <p className="mob-credits-empty-activity">No transactions in this category.</p>
                    ) : (
                        filteredTransactions.map((txn) => (
                            <div key={txn.id} className="mob-credits-txn">
                                <div
                                    className={`mob-credits-txn__icon mob-credits-txn__icon--${txn.type}`}
                                    aria-hidden
                                >
                                    <i
                                        className={`bi bi-${
                                            txn.type === 'credit' ? 'plus-lg' : 'dash-lg'
                                        }`}
                                    />
                                </div>
                                <div className="mob-credits-txn__main">
                                    <p className="mob-credits-txn__title">{txn.title}</p>
                                    <p className="mob-credits-txn__date">
                                        {txn.date}
                                        {txn.orderNumber ? ` · ${txn.orderNumber}` : ''}
                                    </p>
                                    <CreditTransactionDetails
                                        items={txn.items}
                                        className="credit-txn-items--mob"
                                    />
                                </div>
                                <span
                                    className={`mob-credits-txn__amount mob-credits-txn__amount--${txn.type}`}
                                >
                                    {txn.type === 'credit' ? '+' : '−'}₹
                                    {txn.amount.toLocaleString('en-IN')}
                                </span>
                            </div>
                        ))
                    )}
                </section>

                <div className="mob-credits-sticky-wrap mob-credits-sticky-wrap--active">
                    <div className="mob-credits-sticky-wrap__content">
                        <section className="mob-account-card mob-credits-card">
                            <h2 className="mob-account-card__title">
                                <i className="bi bi-bag-check" aria-hidden />
                                How to use at checkout
                            </h2>
                            <ol className="mob-credits-steps">
                                {CHECKOUT_STEPS.map((item) => (
                                    <li key={item.step} className="mob-credits-steps__item">
                                        <span className="mob-credits-steps__num">{item.step}</span>
                                        <span className="mob-credits-steps__text">{item.text}</span>
                                    </li>
                                ))}
                            </ol>
                        </section>

                        <section className="mob-account-card mob-credits-card">
                            <h2 className="mob-account-card__title">
                                <i className="bi bi-graph-up-arrow" aria-hidden />
                                Ways to earn more
                            </h2>
                            <div className="mob-credits-earn-list">
                                {EARN_OPPORTUNITIES.map((item) => (
                                    <article key={item.id} className="mob-credits-earn">
                                        <div className="mob-credits-earn__icon" aria-hidden>
                                            <i className={`bi ${item.icon}`} />
                                        </div>
                                        <div className="mob-credits-earn__body">
                                            <div className="mob-credits-earn__head">
                                                <h3 className="mob-credits-earn__title">{item.title}</h3>
                                                <span className="mob-credits-earn__badge">{item.badge}</span>
                                            </div>
                                            <p className="mob-credits-earn__desc">{item.description}</p>
                                            {item.path && item.cta && (
                                                <button
                                                    type="button"
                                                    className="mob-credits-earn__link"
                                                    onClick={() => navigate(item.path)}
                                                >
                                                    {item.cta}
                                                    <i className="bi bi-chevron-right" aria-hidden />
                                                </button>
                                            )}
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>

                        <section className="mob-account-card mob-credits-card">
                            <h2 className="mob-account-card__title">
                                <i className="bi bi-info-circle" aria-hidden />
                                Good to know
                            </h2>
                            <ul className="mob-credits-faq">
                                {GOOD_TO_KNOW.map((item) => (
                                    <li key={item.title} className="mob-credits-faq__item">
                                        <div className="mob-credits-faq__icon" aria-hidden>
                                            <i className={`bi ${item.icon}`} />
                                        </div>
                                        <div>
                                            <p className="mob-credits-faq__title">{item.title}</p>
                                            <p className="mob-credits-faq__text">{item.text}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </div>

                    <footer className="mob-credits-footer">
                        <button
                            type="button"
                            className="mob-credits-footer__cta"
                            onClick={() => navigate('/')}
                        >
                            Start shopping
                        </button>
                    </footer>
                </div>
            </div>
        </MobAccountPageShell>
    );
}
